import React, { useEffect, useRef, useMemo } from 'react';
import { InstrumentType } from '../../types/composition';
import { announceToScreenReader } from '../../utils/accessibility';
import { instrumentDatabase } from '../../services/instruments/InstrumentDatabase';
import './TrackHeader.css';

/**
 * Props for TrackHeader component
 * 
 * Requirements: 5.1, 5.2, 5.3, 5.4, 5.5, 5.6, 5.7, 5.8, 5.9, 5.10, 16.1, 16.2, 16.3, 16.5
 */
export interface TrackHeaderProps {
  /** Track index (0-based) */
  trackIndex: number;
  /** Track name/label */
  trackName: string;
  /** Instrument type for this track */
  instrumentName: InstrumentType;
  /** Whether the track is muted */
  isMuted: boolean;
  /** Callback when track info (name/instrument) is tapped - opens Tracks panel */
  onTrackInfoTap: () => void;
  /** Callback when mute button is toggled */
  onMuteToggle: () => void;
  /** Callback when volume icon is tapped - opens Volume bottom sheet */
  onVolumeTap: () => void;
  /** Callback when edit button is tapped - opens Sound panel */
  onEditTap: () => void;
  /** Callback when more actions button is tapped - shows track actions menu */
  onMoreActionsTap: () => void;
}

/**
 * TrackHeader Component
 * 
 * Displays essential track information and actions above the step grid in mobile layout.
 * Shows track name, instrument, and provides quick access to mute, volume, sound editing,
 * and additional track actions. All buttons meet the 44x44px minimum touch target size.
 * 
 * Requirements: 5.1, 5.2, 5.3, 5.4, 5.5, 5.6, 5.7, 5.8, 5.9, 5.10, 16.1, 16.2, 16.3, 16.5
 */
export const TrackHeader: React.FC<TrackHeaderProps> = ({
  trackIndex,
  trackName,
  instrumentName,
  isMuted,
  onTrackInfoTap,
  onMuteToggle,
  onVolumeTap,
  onEditTap,
  onMoreActionsTap,
}) => {
  const previousMutedRef = useRef<boolean>(isMuted);

  // Get instrument display name from database
  const instrumentDisplayName = useMemo(() => {
    const metadata = instrumentDatabase.getInstrument(instrumentName);
    return metadata?.displayName || instrumentName;
  }, [instrumentName]);

  /**
   * Announce mute state changes to screen readers
   * Requirement 16.5: Screen reader announcements for state changes
   */
  useEffect(() => {
    if (previousMutedRef.current !== isMuted) {
      announceToScreenReader(isMuted ? `${trackName} muted` : `${trackName} unmuted`);
      previousMutedRef.current = isMuted;
    }
  }, [isMuted, trackName]);

  /**
   * Handle keyboard navigation for mute button
   * Requirement 16.3: Keyboard navigation support
   */
  const handleMuteKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onMuteToggle();
    }
  };

  return (
    <div className="track-header" role="toolbar" aria-label={`Track ${trackIndex + 1} controls`}>
      {/* Track Info - Tappable to open Tracks panel - Requirements 5.1, 5.2, 5.3 */}
      <button
        className="track-info"
        onClick={onTrackInfoTap}
        aria-label={`${trackName}: ${instrumentDisplayName}. Tap to view all tracks`}
        title="Tap to view all tracks"
      >
        <span className="track-info-name">{trackName}</span>
        <span className="track-info-instrument">{instrumentDisplayName}</span>
      </button>

      {/* Action Buttons Container */}
      <div className="track-header-actions">
        {/* Mute Button - Requirement 5.4, 5.5 */}
        <button
          className={`track-action-btn track-action-mute ${isMuted ? 'active' : ''}`}
          onClick={onMuteToggle}
          onKeyDown={handleMuteKeyDown}
          aria-label={isMuted ? 'Unmute track' : 'Mute track'}
          aria-pressed={isMuted}
          title={isMuted ? 'Unmute track' : 'Mute track'}
        >
          <i className={`fa-solid ${isMuted ? 'fa-volume-xmark' : 'fa-volume-high'}`} aria-hidden="true"></i>
        </button>

        {/* Volume Icon - Requirement 5.6 */}
        <button
          className="track-action-btn track-action-volume"
          onClick={onVolumeTap}
          aria-label="Adjust volume"
          title="Adjust volume"
        >
          <i className="fa-solid fa-sliders" aria-hidden="true"></i>
        </button>

        {/* Edit Button - Requirement 5.7, 5.8 */}
        <button
          className="track-action-btn track-action-edit"
          onClick={onEditTap}
          aria-label="Edit sound"
          title="Edit sound parameters"
        >
          <i className="fa-solid fa-music" aria-hidden="true"></i>
        </button>

        {/* More Actions Button - Requirement 5.9, 5.10 */}
        <button
          className="track-action-btn track-action-more"
          onClick={onMoreActionsTap}
          aria-label="More actions"
          title="More track actions"
        >
          <i className="fa-solid fa-ellipsis-vertical" aria-hidden="true"></i>
        </button>
      </div>
    </div>
  );
};
